import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, FileText, Download, Ship, Plane, Filter, DollarSign, Calendar } from "lucide-react";
import html2pdf from "html2pdf.js";

export default function InformeLau() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  
  // Filtros
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [selectedYear, setSelectedYear] = useState(""); // Año seleccionado
  const [tipoEnvio, setTipoEnvio] = useState("ambos"); // maritimo, aereo, ambos
  const [mostrarDatosEconomicos, setMostrarDatosEconomicos] = useState(false);
  const [logos, setLogos] = useState([]); // Logos desde configuración
  
  // Resultados
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Años disponibles para selección rápida
  const currentYear = new Date().getFullYear();
  const availableYears = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    fetchFilterData();
  }, []);

  // Cuando se cargan los clientes, seleccionarlos todos por defecto
  useEffect(() => {
    if (clients.length > 0 && selectedClients.length === 0) {
      setSelectedClients(clients.map(c => c.id));
    }
  }, [clients]);

  // Cuando se cargan los proveedores, preseleccionar solo Invermatis y LG Global
  useEffect(() => {
    if (suppliers.length > 0 && selectedSuppliers.length === 0) {
      const defaultSuppliers = suppliers
        .filter(s => 
          s.nombre.toLowerCase().includes("invermatis") || 
          s.nombre.toLowerCase().includes("lg global")
        )
        .map(s => s.id);
      setSelectedSuppliers(defaultSuppliers);
    }
  }, [suppliers]);

  const fetchFilterData = async () => {
    try {
      const [clientsRes, suppliersRes, productsRes, configRes] = await Promise.all([
        api.get("/clients"),
        api.get("/suppliers"),
        api.get("/products"),
        api.get("/config"),
      ]);
      setClients(clientsRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
      setLogos(configRes.data.logos || []);
    } catch (error) {
      toast.error("Error cargando datos de filtros");
    }
  };

  const handleClientToggle = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSupplierToggle = (supplierId) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId) 
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleProductToggle = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllClients = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c.id));
    }
  };

  const selectAllSuppliers = () => {
    if (selectedSuppliers.length === suppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(suppliers.map(s => s.id));
    }
  };

  const selectAllProducts = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  // Manejar selección de año
  const handleYearSelect = (year) => {
    setSelectedYear(year);
    if (year && year !== "custom") {
      setFechaDesde(`${year}-01-01`);
      setFechaHasta(`${year}-12-31`);
    } else {
      // Si selecciona personalizado, limpiar para que el usuario elija
      setFechaDesde("");
      setFechaHasta("");
    }
  };

  const handleGenerarInforme = async () => {
    if (selectedClients.length === 0) {
      toast.error("Selecciona al menos un cliente");
      return;
    }
    if (!fechaDesde || !fechaHasta) {
      toast.error("Indica las fechas del informe");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/reports/informe-lau", {
        cliente_ids: selectedClients,
        proveedor_ids: selectedSuppliers.length > 0 ? selectedSuppliers : null,
        producto_ids: selectedProducts.length > 0 ? selectedProducts : null,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        tipo_envio: tipoEnvio === "ambos" ? null : tipoEnvio,
        incluir_datos_economicos: mostrarDatosEconomicos,
      });
      setResultados(response.data.lotes || []);
      setShowResults(true);
      
      if (response.data.lotes?.length === 0) {
        toast.info("No se encontraron lotes con los filtros indicados");
      }
    } catch (error) {
      toast.error("Error generando el informe");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      if (dateStr.includes("-")) {
        const [y, m, d] = dateStr.split("-");
        return `${d}/${m}/${y}`;
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat('es-ES', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(value) + " $";
  };

  // Obtener nombre corto para PDF
  const getShortNameForPdf = (nombre) => {
    if (nombre.toLowerCase().includes("invermatis")) return "Invermatis";
    if (nombre.toLowerCase().includes("lg global")) return "LG Global";
    if (nombre.toLowerCase().includes("frisur")) return "Frisur";
    return nombre.split(",")[0].split(" ").slice(0, 2).join(" ");
  };

  // Obtener texto de filtros para pie de página
  const getFiltersSummary = () => {
    const clientNames = selectedClients.length === clients.length
      ? "Todos"
      : clients.filter(c => selectedClients.includes(c.id)).map(c => c.nombre).slice(0, 3).join(", ") + (selectedClients.length > 3 ? "..." : "");
    const provNames = selectedSuppliers.length === suppliers.length || selectedSuppliers.length === 0
      ? "Todos"
      : suppliers.filter(s => selectedSuppliers.includes(s.id)).map(s => getShortNameForPdf(s.nombre)).join(", ");
    const tipoText = tipoEnvio === "maritimo" ? "Marítimo" : tipoEnvio === "aereo" ? "Aéreo" : "Todos";
    return `Clientes: ${clientNames} | Proveedores: ${provNames} | Tipo: ${tipoText} | Período: ${formatDate(fechaDesde)} - ${formatDate(fechaHasta)}`;
  };

  const handleDownloadPdf = async () => {
    const element = document.getElementById("informe-lau-container");
    if (!element) return;

    // Formato: AAAAMMDD Lotes PROVEEDOR/ES RANGOFECHAS.pdf
    const fechaHoy = new Date().toISOString().split("T")[0].replace(/-/g, "");
    
    // Obtener nombres de proveedores seleccionados (nombres cortos con espacios)
    const proveedoresNombres = selectedSuppliers.length === suppliers.length || selectedSuppliers.length === 0
      ? "TODOS"
      : suppliers
          .filter(s => selectedSuppliers.includes(s.id))
          .map(s => getShortNameForPdf(s.nombre))
          .join(" ");
    
    // Rango de fechas con guion
    const rangoFechas = `${fechaDesde.replace(/-/g, "")}-${fechaHasta.replace(/-/g, "")}`;
    
    const filename = `${fechaHoy} Lotes ${proveedoresNombres} ${rangoFechas}`;

    const opt = {
      margin: [5, 5, 12, 5], // Aumentar margen inferior para pie de página
      filename: `${filename}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, width: 794 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all' }
    };

    try {
      const pdf = await html2pdf().set(opt).from(element).toPdf().get('pdf');
      const totalPages = pdf.internal.getNumberOfPages();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const fechaInforme = new Date().toLocaleDateString('es-ES');
      const filterText = getFiltersSummary();
      
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(100);
        const footerText = `Seguimiento de Lotes | ${filterText} | Generado: ${fechaInforme} | Página ${i}/${totalPages}`;
        pdf.text(footerText, pageWidth / 2, pageHeight - 4, { align: 'center' });
      }
      
      pdf.save(`${filename}.pdf`);
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error generando el PDF");
    }
  };

  // Calcular totales
  const totalKilosNetos = resultados.reduce((sum, r) => sum + (r.peso_neto || 0), 0);
  const totalKilosBrutos = resultados.reduce((sum, r) => sum + (r.peso_bruto || 0), 0);
  const totalFactura = resultados.reduce((sum, r) => sum + (r.importe_factura_usd || 0), 0);

  // Obtener logos de proveedores seleccionados desde la configuración
  const getSelectedSuppliersLogos = () => {
    const suppliersToShow = selectedSuppliers.length === 0 
      ? suppliers 
      : suppliers.filter(s => selectedSuppliers.includes(s.id));
    
    const baseUrl = BACKEND_URL;
    
    return suppliersToShow.map(s => {
      // Buscar el logo en la configuración por entidad_id (id del proveedor)
      const logoConfig = logos.find(l => l.entidad_id === s.id && l.tipo === 'proveedor');
      return {
        nombre: s.nombre,
        logo: logoConfig ? `${baseUrl}${logoConfig.url}` : null
      };
    });
  };

  // Texto para tipo de envío
  const getTipoEnvioText = () => {
    if (tipoEnvio === "maritimo") return "Envíos marítimos";
    if (tipoEnvio === "aereo") return "Envíos aéreos";
    return "Envíos marítimos y aéreos";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reports")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Seguimiento de Lotes</h1>
            <p className="text-slate-500 mt-1">Informe de lotes por cliente y proveedor</p>
          </div>
        </div>
      </div>

      {/* Filtros */}
      {!showResults && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Configurar Informe
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Clientes */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Clientes (empresas compradoras)</Label>
                <Button variant="outline" size="sm" onClick={selectAllClients}>
                  {selectedClients.length === clients.length ? "Deseleccionar todos" : "Seleccionar todos"}
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-lg max-h-48 overflow-y-auto">
                {clients.map((client) => (
                  <div key={client.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`client-${client.id}`}
                      checked={selectedClients.includes(client.id)}
                      onCheckedChange={() => handleClientToggle(client.id)}
                    />
                    <Label htmlFor={`client-${client.id}`} className="text-sm cursor-pointer">
                      {client.nombre}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {selectedClients.length} de {clients.length} seleccionados
              </p>
            </div>

            {/* Fechas con selector de año */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Período del informe</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Selector de año rápido */}
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600">Selección rápida</Label>
                  <Select value={selectedYear} onValueChange={handleYearSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar año..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Personalizado</SelectItem>
                      {availableYears.map((year) => (
                        <SelectItem key={year} value={year.toString()}>
                          Año {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600">Fecha desde</Label>
                  <Input
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => {
                      setFechaDesde(e.target.value);
                      setSelectedYear("custom");
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600">Fecha hasta</Label>
                  <Input
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => {
                      setFechaHasta(e.target.value);
                      setSelectedYear("custom");
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Tipo de envío */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Tipo de envío</Label>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="tipo-ambos"
                    checked={tipoEnvio === "ambos"}
                    onCheckedChange={() => setTipoEnvio("ambos")}
                  />
                  <Label htmlFor="tipo-ambos" className="cursor-pointer">Ambos</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="tipo-maritimo"
                    checked={tipoEnvio === "maritimo"}
                    onCheckedChange={() => setTipoEnvio("maritimo")}
                  />
                  <Label htmlFor="tipo-maritimo" className="cursor-pointer flex items-center gap-1">
                    <Ship className="w-4 h-4" /> Marítimo
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="tipo-aereo"
                    checked={tipoEnvio === "aereo"}
                    onCheckedChange={() => setTipoEnvio("aereo")}
                  />
                  <Label htmlFor="tipo-aereo" className="cursor-pointer flex items-center gap-1">
                    <Plane className="w-4 h-4" /> Aéreo
                  </Label>
                </div>
              </div>
            </div>

            {/* Proveedores */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Proveedores</Label>
                <Button variant="outline" size="sm" onClick={selectAllSuppliers}>
                  {selectedSuppliers.length === suppliers.length ? "Deseleccionar todos" : "Seleccionar todos"}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4 bg-slate-50 rounded-lg max-h-64 overflow-y-auto">
                {suppliers.map((supplier) => {
                  // Buscar logo en configuración
                  const logoConfig = logos.find(l => l.entidad_id === supplier.id && l.tipo === 'proveedor');
                  const baseUrl = BACKEND_URL;
                  const logoUrl = logoConfig ? `${baseUrl}${logoConfig.url}` : null;
                  
                  return (
                    <div 
                      key={supplier.id} 
                      className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-all ${
                        selectedSuppliers.includes(supplier.id) 
                          ? 'bg-blue-50 border-blue-300' 
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleSupplierToggle(supplier.id)}
                    >
                      <Checkbox
                        id={`supplier-${supplier.id}`}
                        checked={selectedSuppliers.includes(supplier.id)}
                        onCheckedChange={() => handleSupplierToggle(supplier.id)}
                      />
                      {logoUrl ? (
                        <img 
                          src={logoUrl} 
                          alt={supplier.nombre}
                          className="h-8 w-auto object-contain"
                        />
                      ) : (
                        <div className="h-8 w-8 bg-slate-200 rounded flex items-center justify-center text-xs font-bold text-slate-500">
                          {supplier.nombre?.charAt(0) || "P"}
                        </div>
                      )}
                      <Label htmlFor={`supplier-${supplier.id}`} className="text-sm cursor-pointer flex-1">
                        {supplier.nombre}
                      </Label>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500">
                {selectedSuppliers.length} de {suppliers.length} seleccionados
              </p>
            </div>

            {/* Productos */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Productos (opcional)</Label>
                <Button variant="outline" size="sm" onClick={selectAllProducts}>
                  {selectedProducts.length === products.length ? "Deseleccionar todos" : "Seleccionar todos"}
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-4 bg-slate-50 rounded-lg max-h-48 overflow-y-auto">
                {products.map((product) => (
                  <div 
                    key={product.id} 
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all text-sm ${
                      selectedProducts.includes(product.id) 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleProductToggle(product.id)}
                  >
                    <Checkbox
                      id={`product-lau-${product.id}`}
                      checked={selectedProducts.includes(product.id)}
                      onCheckedChange={() => handleProductToggle(product.id)}
                    />
                    <Label htmlFor={`product-lau-${product.id}`} className="text-xs cursor-pointer flex-1 truncate">
                      {product.nombre}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {selectedProducts.length === 0 
                  ? "Sin filtro de producto (todos)" 
                  : `${selectedProducts.length} de ${products.length} seleccionados`}
              </p>
            </div>

            {/* Opción de datos económicos */}
            <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="datos-economicos"
                  checked={mostrarDatosEconomicos}
                  onCheckedChange={(checked) => setMostrarDatosEconomicos(checked)}
                />
                <Label htmlFor="datos-economicos" className="cursor-pointer flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                  <span className="font-medium">Incluir datos económicos</span>
                </Label>
              </div>
              <p className="text-xs text-amber-700 ml-7">
                Se añadirá la columna "Factura" con el importe de la factura origen en dólares (USD)
              </p>
            </div>

            {/* Botón generar */}
            <div className="flex justify-end pt-4">
              <Button 
                onClick={handleGenerarInforme} 
                disabled={loading}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {loading ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2" />
                    Generando...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generar Informe
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resultados */}
      {showResults && (
        <div className="space-y-4">
          {/* Acciones */}
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => setShowResults(false)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Modificar filtros
            </Button>
            <Button onClick={handleDownloadPdf} className="bg-emerald-600 hover:bg-emerald-700">
              <Download className="w-4 h-4 mr-2" />
              Descargar PDF
            </Button>
          </div>

          {/* Tabla de resultados */}
          <div id="informe-lau-container" className="bg-white p-6 rounded-lg border">
            {/* Encabezado del informe */}
            <div className="mb-6 border-b pb-4">
              {/* Logos de proveedores */}
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                {getSelectedSuppliersLogos().map((supplier, idx) => (
                  <div key={idx} className="flex items-center">
                    {supplier.logo ? (
                      <img 
                        src={supplier.logo} 
                        alt={supplier.nombre}
                        crossOrigin="anonymous"
                        className="h-12 object-contain max-w-[150px]"
                        onError={(e) => {
                          // Si falla la carga del logo, mostrar el nombre
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="h-12 px-4 bg-slate-100 rounded items-center justify-center text-sm font-medium text-slate-600"
                      style={{ display: supplier.logo ? 'none' : 'flex' }}
                    >
                      {supplier.nombre}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Título y detalles */}
              <h2 className="text-xl font-bold text-slate-900 mb-2">Seguimiento de lotes</h2>
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Fechas: {formatDate(fechaDesde)} - {formatDate(fechaHasta)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {tipoEnvio === "maritimo" ? <Ship className="w-4 h-4" /> : 
                   tipoEnvio === "aereo" ? <Plane className="w-4 h-4" /> : 
                   <><Ship className="w-4 h-4" /><Plane className="w-4 h-4" /></>}
                  <span>{getTipoEnvioText()}</span>
                </div>
              </div>
            </div>

            {resultados.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No se encontraron lotes con los filtros indicados
              </div>
            ) : (
              <>
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="bg-slate-100 font-semibold text-xs p-2">Lote</TableHead>
                      <TableHead className="bg-slate-100 font-semibold text-xs p-2">Producto</TableHead>
                      <TableHead className="bg-slate-100 font-semibold text-xs p-2 text-right">Kg Netos</TableHead>
                      <TableHead className="bg-slate-100 font-semibold text-xs p-2 text-right">Kg Brutos</TableHead>
                      <TableHead className="bg-slate-100 font-semibold text-xs p-2">Contenedor</TableHead>
                      <TableHead className="bg-slate-100 font-semibold text-xs p-2">F. Embarque</TableHead>
                      {mostrarDatosEconomicos && (
                        <TableHead className="bg-slate-100 font-semibold text-xs p-2 text-right">Factura $</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultados.map((lote) => (
                      <TableRow key={lote.id}>
                        <TableCell className="font-medium text-xs p-2">{lote.numero_lote}</TableCell>
                        <TableCell className="text-xs p-2">{lote.producto_nombre}</TableCell>
                        <TableCell className="text-xs p-2 text-right">
                          {(lote.peso_neto || 0).toLocaleString('es-ES', { minimumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell className="text-xs p-2 text-right">
                          {(lote.peso_bruto || 0).toLocaleString('es-ES', { minimumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell className="text-xs p-2">{lote.contenedor || "-"}</TableCell>
                        <TableCell className="text-xs p-2">{formatDate(lote.fecha_tentativa_embarque)}</TableCell>
                        {mostrarDatosEconomicos && (
                          <TableCell className="text-xs p-2 text-right">
                            {formatCurrency(lote.importe_factura_usd)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {/* Fila de totales */}
                    <TableRow className="bg-slate-100 font-bold">
                      <TableCell className="text-xs p-2">TOTAL</TableCell>
                      <TableCell className="text-xs p-2">{resultados.length} lotes</TableCell>
                      <TableCell className="text-xs p-2 text-right">
                        {totalKilosNetos.toLocaleString('es-ES', { minimumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className="text-xs p-2 text-right">
                        {totalKilosBrutos.toLocaleString('es-ES', { minimumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className="text-xs p-2" colSpan={mostrarDatosEconomicos ? 1 : 2}></TableCell>
                      {mostrarDatosEconomicos && (
                        <>
                          <TableCell className="text-xs p-2"></TableCell>
                          <TableCell className="text-xs p-2 text-right">
                            {formatCurrency(totalFactura)}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
